import { useEffect, useMemo, useState } from 'react';
import type { CwWorkTypeBillingClassAliasDto } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useCwWorkTypeBillingClassAliases, useSetCwWorkTypeBillingClassAliases } from './api.js';

interface AliasRow {
  /** Stable client-side key so rows keep focus while editing. */
  id: string;
  cwWorkTypeName: string;
  billingClassId: string;
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `worktype-billing-alias-${aliasSeq}`;
}

interface WorkTypeBillingClassAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise time-entry work type (a dropdown) onto a PSA billing class (a
 * dropdown). This mapping applies to AGREEMENT time only — project and project-ticket
 * hours keep their project/phase billing class and are never affected. Agreement hours
 * whose work type has no row here fall back to the default billing class (the sync never
 * fails on a missing alias).
 */
export function WorkTypeBillingClassAliasEditor({
  isOpen,
}: WorkTypeBillingClassAliasEditorProps): JSX.Element {
  const configQuery = useCwWorkTypeBillingClassAliases(isOpen);
  const saveAliases = useSetCwWorkTypeBillingClassAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwWorkTypeName: alias.cwWorkTypeName,
        billingClassId: alias.billingClassId,
      })),
    );
  }, [config]);

  const billingClasses = config?.billingClasses ?? [];
  const discoveredWorkTypes = config?.discoveredWorkTypes ?? [];
  const live = config?.live ?? false;

  // CW work-type names already mapped, so we can hint which discovered ones remain.
  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwWorkTypeName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredWorkTypes.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (cwWorkTypeName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwWorkTypeName, billingClassId: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwWorkTypeBillingClassAliasDto[] = rows
      .filter((row) => row.cwWorkTypeName.trim() && row.billingClassId)
      .map((row) => ({
        cwWorkTypeName: row.cwWorkTypeName.trim(),
        billingClassId: row.billingClassId,
      }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-work-types-billing';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Billing class from work type</h3>
        <p className="mt-1 text-sm text-slate-500">
          ConnectWise stores a time entry&apos;s work type as a dropdown. Map each ConnectWise work
          type to a PSA billing class below. On sync, the matched billing class is set on the time
          entry — but <strong>only for time on agreements</strong> (support/agreement hours).
          Project and project-ticket hours keep their project&apos;s billing class. Agreement hours
          whose work type has no row here fall back to the default billing class.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading work types…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load work types.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise work types loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise work types discovered yet. Configure the connection to load suggestions
              — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredWorkTypes.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the work-type billing-class aliases. Make sure each row targets a valid
              billing class.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">
              No work-type billing-class aliases yet. Add one below.
            </p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise work type</th>
                  <th className="py-2 pr-3 font-medium">PSA billing class</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwWorkTypeName}
                        placeholder="e.g. Remote Support"
                        onChange={(e) => updateRow(row.id, { cwWorkTypeName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.billingClassId}
                        onChange={(e) => updateRow(row.id, { billingClassId: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select billing class —</option>
                        {billingClasses.map((billingClass) => (
                          <option key={billingClass.id} value={billingClass.id}>
                            {billingClass.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save billing classes
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
